import moment from 'moment-timezone';

import { engagementMessages, remindersTypes } from '../../modals/EditRpmSchedule/EditRpmScheduleModal';
import Strings from '../../Strings';

const allTimeZones = moment.tz.names();

export const scheduleTypes = {
  everyDay: 'daily',
  asNeeded: 'as_needed',
};

const MAX_MISSED_DOSES_DAYS = 365;

export const validateSchedule = (schedule, allowedTagsForReminders, scheduleEnded) => {
  const errors = validateMedication(schedule.name)
    .concat(validateType(schedule.type))
    .concat(validateEndDate(schedule.end_time, schedule.timezone, scheduleEnded))
    .concat(validateTimezone(schedule.type, schedule.timezone))
    .concat(
      validateOffset(
        schedule.type,
        schedule.offset,
        schedule.take_window,
        schedule.invalidFields?.filter(f => f.field === 'offset'),
      ),
    )
    .concat(validateAdministrativeWindow(schedule.offset, schedule.take_window))
    .concat(validateReminders(schedule.sms_reminder, allowedTagsForReminders, schedule.offset?.length))
    .concat(validateMilestoneReminders(schedule.milestone_reminders, allowedTagsForReminders))
  return errors;
};

function removeNonErrors(errors) {
  return errors.filter(e => e.missing || e.errors?.length > 0);
}

export function validateMedication(name) {
  if (!name) {
    return [{ property: 'name', propertyName: Strings.capPatient.medication, errors: [], missing: true }];
  }
  return [];
}

export function validateType(type) {
  const ret = { property: 'type', propertyName: Strings.capPatient.type, errors: [], missing: false };

  if (!type) {
    ret.missing = true;
  } else if (!Object.values(scheduleTypes).includes(type)) {
    ret.errors.push('Unknown schedule type');
  }

  return removeNonErrors([ret]);
}

export function validateEndDate(date, timezone, scheduleEnded) {
  const ret = { property: 'end_time', propertyName: Strings.capPatient.endDate, errors: [], missing: false };
  if (
    date &&
    moment
      .tz(date, timezone)
      .endOf('day')
      .unix() <
      moment
        .tz(timezone)
        .endOf('day')
        .unix()
  ) {
    if (!scheduleEnded) {
      ret.errors.push(Strings.errors.endDateMustBeTomorrow);
    }
  }
  return removeNonErrors([ret]);
}

export function validateTimezone(type, timezone) {
  const ret = { property: 'timezone', propertyName: Strings.timezone, errors: [], missing: false };
  if (type && !timezone) {
    ret.missing = true;
  } else if (timezone && !allTimeZones.includes(timezone)) ret.errors.push('Unknown timezone');
  return removeNonErrors([ret]);
}

export function validateOffset(type, offset, window, invalidFields) {
  if (type === scheduleTypes.everyDay && (!offset || offset.length < 1)) {
    return [{ property: 'offset', propertyName: Strings.capPatient.takeTimes, errors: [], missing: true }];
  }
  if (type !== scheduleTypes.everyDay && (!offset || offset.length < 1)) {
    return [];
  }

  const ret = [];
  const windowStart = window && window.length > 0 ? window[0] : null;
  const windowEnd = window && window.length > 1 ? window[1] : null;

  if (offset.length > 6) {
    ret.push({
      property: 'timesPerDay',
      propertyName: Strings.capPatient.takeTimes,
      errors: ['Maximum takes per day is 6'],
      missing: false,
    });
  }

  const secondsInDay = 24 * 60 * 60;
  offset.forEach((o, i) => {
    if (o === null || o === undefined || o === '') {
      ret.push({
        property: 'offset',
        propertyName: Strings.capPatient.takeTimes,
        index: i,
        missing: true,
      });
    } else {
      const r = { property: 'offset', propertyName: Strings.capPatient.takeTimes, index: i, errors: [], missing: false };

      if (offset.filter(off => off === o)?.length > 1) {
        r.errors.push(Strings.errors.duplicateTimeForTake);
      }

      ret.push(r);

      if (windowStart && windowEnd) {
        const minDiff = windowStart + windowEnd;
        if (
          offset.some(
            off =>
              off !== o &&
              ((o - off + secondsInDay) % secondsInDay < minDiff ||
                (off - o + secondsInDay) % secondsInDay < minDiff),
          )
        ) {
          ret.push({
            property: 'offset',
            propertyName: Strings.capPatient.takeTimes,
            index: offset.findIndex(off => off === o),
            errors: ['Time too close to another take. Change time or administrative window'],
            missing: false,
          });
        }
      }
    }
  });

  if (invalidFields) {
    invalidFields.forEach(f => {
      ret.push({
        property: f.field,
        propertyName: Strings.capPatient.takeTimes,
        index: f.index,
        errors: [Strings.capPatient.incorrectFieldFormat],
        missing: false,
      });
    });
  }

  return removeNonErrors(ret);
}

export function validateAdministrativeWindow(offset, window) {
  const windowStart = window && window.length > 0 ? window[0] : null;
  const windowEnd = window && window.length > 1 ? window[1] : null;
  const ret = [];

  if (offset && offset.length > 1) {
    if (!windowStart) {
      ret.push({ property: 'take_window', propertyName: Strings.administrationWindow, field: 'start', missing: true });
    }
    if (!windowEnd) {
      ret.push({ property: 'take_window', propertyName: Strings.administrationWindow, field: 'end', missing: true });
    }
  } else {
    if (windowEnd && !windowStart) {
      ret.push({ property: 'take_window', propertyName: Strings.administrationWindow, field: 'start', missing: true });
    }
    if (windowStart && !windowEnd) {
      ret.push({ property: 'take_window', propertyName: Strings.administrationWindow, field: 'end', missing: true });
    }
  }

  return removeNonErrors(ret);
}

export function validateReminders(reminders, allowedTagsForReminders, takesPerDay) {
  const ret = [];

  if (reminders) {
    Object.entries(reminders).forEach(([type, r]) => {
      if (r?.checked) {
        if ((type === remindersTypes.early || type === remindersTypes.late) && !r.offset) {
          ret.push({ property: 'sms_reminder', propertyName: Strings.patient_medications.reminders[type], type, field: 'offset', missing: true });
        }
        if (
          type === remindersTypes.missedDoses &&
          takesPerDay &&
          r.offset &&
          r.offset > MAX_MISSED_DOSES_DAYS * takesPerDay
        ) {
          ret.push({
            property: 'sms_reminder',
            propertyName: Strings.patient_medications.reminders[type],
            type,
            field: 'offset',
            missing: false,
            errors: [Strings.formatString(Strings.errors.maxValueExceeded, MAX_MISSED_DOSES_DAYS * takesPerDay)],
          });
        }
        if (!r.text) {
          ret.push({
            property: 'sms_reminder',
            propertyName: Strings.formatString(Strings.patient_medications.messageFor, Strings.patient_medications.reminders[type] || type),
            type,
            field: 'text',
            missing: true,
          });
        } else {
          const tags = r.text.match(/\[.*?\]/g);
          if (tags && allowedTagsForReminders[type]) {
            tags.forEach(tag => {
              const tagValue = tag.replace(/\[|\]/g, '');
              if (!allowedTagsForReminders[type].includes(tagValue)) {
                ret.push({
                  property: 'sms_reminder',
                  propertyName: Strings.formatString(Strings.patient_medications.messageFor, Strings.patient_medications.reminders[type] || type),
                  type,
                  field: 'text',
                  missing: false,
                  errors: [`${Strings.invalidTag}: ${tag}`],
                });
              }
            });
          }
        }
      }
    });
  }

  return removeNonErrors(ret);
}

export function validateMilestoneReminders(reminders, allowedTagsForReminders) {
  const ret = [];
  if (reminders) {
    Object.entries(reminders).filter(([type, r]) => type !== "milestone_rules").forEach(([type, r]) => {
      if (r?.checked) {
        if ((type === engagementMessages.streak_achievement) && !r.thresholds?.length) {
          ret.push({ property: 'milestone_reminders', propertyName: Strings.patient_medications.reminders[type], type, field: 'thresholds', missing: true });
        }
        if (type === engagementMessages.streak_achievement && r.thresholds?.length > 0) {
          r.thresholds.forEach((threshold, i) => {
            if (!threshold && threshold !== 0) {
              ret.push({
                property: 'milestone_reminders',
                propertyName: `${Strings.patient_medications.reminders[type]} ${Strings.thresholds}`,
                type,
                field: 'thresholds',
                index: i,
                missing: true,
              });
            } else if (threshold < 1) {
              ret.push({
                property: 'milestone_reminders',
                propertyName: `${Strings.patient_medications.reminders[type]} ${Strings.thresholds}`,
                type,
                field: 'thresholds',
                index: i,
                errors: [Strings.errorMaxBatchSize],
              });
            }
            if (threshold && r.thresholds?.some((t, j) => i !== j && t === threshold)) {
              ret.push({
                property: 'milestone_reminders',
                propertyName: `${Strings.patient_medications.reminders[type]} ${Strings.thresholds}`,
                type,
                field: 'thresholds',
                index: i,
                errors: [Strings.errors.duplicateThreshold],
              });
            }
            if (reminders.streak_achievement?.separateMessagesForThresholds && !reminders.milestone_rules?.streak?.messages?.[threshold]) {
              ret.push({
                property: 'milestone_reminders',
                propertyName: Strings.formatString(Strings.patient_medications.messageFor, Strings.patient_medications.reminders[type]),
                type,
                field: 'milestone_rules',
                missing: true,
              });
            }
              // todo: validate message tags in milestone_rules messages when they are returned from backend
          });
        }
        if (!r.text) {
          ret.push({
            property: 'milestone_reminders',
            propertyName: Strings.formatString(Strings.patient_medications.messageFor, Strings.patient_medications.reminders[type] || type),
            type,
            field: 'text',
            missing: true,
          });
        } else {
          const tags = r.text.match(/\[.*?\]/g);
          if (tags && allowedTagsForReminders[type]) {
            tags.forEach(tag => {
              const tagValue = tag.replace(/\[|\]/g, '');
              if (!allowedTagsForReminders[type].includes(tagValue)) {
                ret.push({
                  property: 'milestone_reminders',
                  propertyName: Strings.formatString(Strings.patient_medications.messageFor, Strings.patient_medications.reminders[type] || type),
                  type,
                  field: 'text',
                  missing: false,
                  errors: [`${Strings.invalidTag}: ${tag}`],
                });
              }
            });
          }
        }
      }
    });

    if (reminders.time_milestones_enabled) {
      [7, 14, 21, 28].forEach(threshold => {
        ['compliant', 'partial'].forEach(status => {
          const messages = reminders.milestone_rules?.time?.messages?.[threshold];
          if (!messages?.[status]) {
            ret.push({
              property: 'milestone_rules',
              propertyName: Strings.formatString(Strings.patient_medications.messageFor, `${status} achievement - ${threshold} ${Strings.days}`),
              field: 'message',
              missing: true,
            });
          } else {
            const tags = messages[status].match(/\[.*?\]/g);
            if (tags && allowedTagsForReminders.time_milestones) {
              tags.forEach(tag => {
                const tagValue = tag.replace(/\[|\]/g, '');
                if (!allowedTagsForReminders.time_milestones.includes(tagValue)) {
                  ret.push({
                    property: 'milestone_rules',
                    propertyName: Strings.formatString(Strings.patient_medications.messageFor, `${status} achievement - ${threshold} ${Strings.days}`),
                    field: 'message',
                    missing: false,
                    errors: [`${Strings.invalidTag}: ${tag}`],
                  });
                }
              });
            }
          }
        });
      });
    }
  }

  return removeNonErrors(ret);
}
